/**
 * @file exportApi.ts
 * @description RTK Query API slice for MQL code preview and download.
 */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RootState } from '../../../app/store.js';

export interface MqlFilePreview {
  filename: string;
  code: string;
  lineCount: number;
}

export interface CodePreviewResponse {
  success: boolean;
  data: {
    strategyName: string;
    mt4: MqlFilePreview;
    mt5: MqlFilePreview;
  };
}

import { createBaseQueryWithReauth } from '../../../shared/api/baseQueryWithReauth.js';
import { subscriptionApi } from '../../subscription/api/subscriptionApi.js';

export const exportApi = createApi({
  reducerPath: 'exportApi',
  baseQuery: createBaseQueryWithReauth('/api/v1/export'),
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getCodePreview: builder.query<CodePreviewResponse, string>({
      query: (id) => `/${id}/preview`,
    }),

    recordExportUsage: builder.mutation<{ success: boolean; message: string; data?: any }, string>({
      query: (id) => ({
        url: `/${id}/record`,
        method: 'POST',
      }),
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          const res = await queryFulfilled;
          if (res.data?.data?.subscription) {
            dispatch(
              subscriptionApi.util.updateQueryData('getSubscriptionSummary', undefined, (draft) => {
                if (draft?.data) {
                  draft.data = res.data.data.subscription;
                }
              })
            );
          }
          dispatch(subscriptionApi.util.invalidateTags(['Subscription']));
        } catch {
          // Request failed
        }
      },
    }),
  }),
});

export const { useGetCodePreviewQuery, useRecordExportUsageMutation } = exportApi;
